import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { GraduationCap, Loader2, UploadCloud, CheckCircle2, CalendarX, AlertTriangle, FileCheck2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import MainSiteLink from "@/components/MainSiteLink";

// Public page a referee reaches from the emailed link; no login, the link itself is the key.
export default function RefereeUpload() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    api.get(`/reference/${token}`)
      .then((r) => setInfo(r.data))
      .catch((err) => setError(formatError(err.response?.data?.detail) || t("referee.invalid")));
  }, [token, t]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setUploadError(t("errors.file_too_large")); return; }
    setUploading(true);
    setUploadError(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/reference/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInfo((i) => ({ ...i, ...res.data }));
    } catch (err) {
      setUploadError(formatError(err.response?.data?.detail) || t("errors.upload_failed"));
    } finally {
      setUploading(false);
    }
  };

  const received = info?.status === "received";

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-lg font-semibold tracking-tight">{t("referee.brand")}</span>
          </div>
          <MainSiteLink className="shrink-0" />
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-10 shadow-sm animate-fade-in" data-testid="referee-card">
          {!info && !error && <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>}

          {error && (
            <div className="text-center py-4" data-testid="referee-invalid">
              <AlertTriangle className="w-8 h-8 text-accent mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground leading-relaxed">{error}</p>
              <p className="text-sm text-muted-foreground mt-4">
                {t("common.questions")} <a href="mailto:contact@penda.org.mk" className="text-primary underline">contact@penda.org.mk</a>
              </p>
            </div>
          )}

          {info && (
            <>
              <p className="text-sm text-muted-foreground mb-1">{t("referee.dear", { name: info.referee_name })}</p>
              <h1 className="font-heading text-2xl font-semibold tracking-tight mb-3">
                {t("referee.title", { name: info.applicant_name })}
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                {t("referee.intro", { applicant: info.applicant_name, slot: info.slot_label })}
              </p>

              {received && (
                <div className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6" data-testid="referee-received">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div className="text-sm leading-relaxed">
                    <Trans i18nKey="referee.received" components={{ b: <b /> }} />
                    {info.original_filename && (
                      <span className="flex items-center gap-1.5 text-muted-foreground mt-1">
                        <FileCheck2 className="w-4 h-4" /> {info.original_filename}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {info.open ? (
                <>
                  <label className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors" data-testid="referee-upload">
                    {uploading ? <Loader2 className="w-6 h-6 text-primary animate-spin" /> : <UploadCloud className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />}
                    <span className="text-sm text-muted-foreground text-center">
                      {uploading ? t("common.uploading") : received ? t("referee.replace") : t("referee.click")}
                    </span>
                    <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" />
                  </label>
                  {uploadError && <p className="text-sm text-destructive mt-3" data-testid="referee-upload-error">{uploadError}</p>}
                  {info.deadline_display && (
                    <p className="text-xs text-muted-foreground mt-4">{t("referee.until", { deadline: info.deadline_display })}</p>
                  )}
                </>
              ) : (
                <div className="flex items-start gap-3 bg-muted/60 border border-border rounded-xl p-4 text-sm">
                  <CalendarX className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-muted-foreground">{t("referee.closed")}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
